import { makeAutoObservable, action, computed } from "mobx";
import moment from "moment";
import * as d3 from "d3";

export class ChartStoreImpl {
  x = () => {};
  userids = [];
  useridsWithDateRange = [];
  timeseries = [];
  rawTimeseries = [];
  events = [];
  height = 10;
  lineValues = {};
  lineValuesRaw = {};
  bounds = [null, null];
  hideEventTable = false;
  resetEventChart = 0;
  currentTimestamp = 0;
  user = null;

  constructor() {
    makeAutoObservable(this, {
      setBounds: action,
      filteredEvents: computed,
      toggleHideTable: action,
      updateResetEventChart: action,
      rawTimeseriesTransformed: computed,
      setCurrentTimestamp: action,
      computedClosestValues: computed,
      timeseriesTimestamps: computed,
      rawTimeseriesTimestamps: computed,
    });
  }
  toggleHideTable() {
    this.hideEventTable = !this.hideEventTable;
  }

  updateResetEventChart() {
    this.resetEventChart += 1;
  }

  setCurrentTimestamp(currentTimestamp) {
    this.currentTimestamp = currentTimestamp;
  }

  setClosestValues(closestXValue, closestDataPoint) {
    const formatted = moment(closestXValue).format("MMMM Do YYYY, h:mm:ss a");
    this.lineValues = {
      max_hr: `${formatted}, ${closestDataPoint["max_hr"]}`,
      sd_ratio: `${formatted}, ${closestDataPoint["sd_ratio"]}`,
      triangular_index: `${formatted}, ${closestDataPoint["triangular_index"]}`,
      stress_index: `${formatted}, ${closestDataPoint["stress_index"]}`,
    };
    this.lineValuesRaw = {
      heart_rate: `${formatted}, ${closestDataPoint["heart_rate"].toFixed(2)}`,
    };
  }

  get timeseriesTimestamps() {
    return this.timeseries.map((x) => new Date(x.timepoint).getTime());
  }

  get rawTimeseriesTimestamps() {
    return this.rawTimeseriesTransformed.map((x) =>
      new Date(x.timepoint).getTime()
    );
  }

  get computedClosestValues() {
    if (!this.currentTimestamp) {
      return {};
    }
    const rawIndex = d3.bisectRight(
      this.rawTimeseriesTimestamps,
      this.currentTimestamp
    );
    const timeseriesIndex = d3.bisectRight(
      this.timeseriesTimestamps,
      this.currentTimestamp
    );
    const closestRaw = this.rawTimeseriesTransformed[rawIndex];
    const closestMetric = this.filteredData[timeseriesIndex];
    const formatted = moment(this.currentTimestamp).format(
      "MMMM Do YYYY, h:mm:ss a"
    );
    let metric = {};

    if (closestRaw) {
      metric["heart_rate"] = `${formatted}, ${closestRaw["heart_rate"].toFixed(
        2
      )}`;
    }
    if (closestMetric) {
      metric = {
        ...metric,
        ...{
          max_hr: `${formatted}, ${closestMetric["max_hr"]}`,
          sd_ratio: `${formatted}, ${closestMetric["sd_ratio"]}`,
          triangular_index: `${formatted}, ${closestMetric["triangular_index"]}`,
          sdnn: `${formatted}, ${closestMetric["sdnn"]}`,
          stress_index: `${formatted}, ${closestMetric["stress_index"]}`,
        },
      };
    }
    return metric;
  }

  setBounds(bounds) {
    this.bounds = bounds;
  }

  get rawTimeseriesTransformed() {
    if (this.rawTimeseries.length > 0) {
      return this.rawTimeseries.map((x) => {
        const obj = {
          id: x.id,
        };
        obj[x.key] = x.value;
        obj["timepoint"] = x.timestamp;
        return obj;
      });
    }
    return [];
  }

  get filteredData() {
    if (
      this.bounds &&
      this.bounds.length === 2 &&
      this.bounds[0] &&
      this.timeseries
    ) {
      return this.timeseries.filter(
        (x) =>
          this.bounds[0] <= new Date(x.sample_start).getTime() &&
          this.bounds[1] >= new Date(x.sample_end).getTime()
      );
    } else {
      return this.timeseries;
    }
  }

  get rects() {
    let x = this.x();
    if (!x) {
      return [];
    }
    if (this.filteredEvents) {
      const eventRects = this.filteredEvents.map((d) => {
        return {
          x: x(new Date(d.eventstart)),
          y: 0,
          w: x(new Date(d.eventend)) - x(new Date(d.eventstart)),
          h: this.height,
          d: d,
        };
      });
      return eventRects;
    }
    return [];
  }

  get filteredEvents() {
    let data;
    if (
      this.bounds &&
      this.bounds.length === 2 &&
      this.bounds[0] &&
      this.events
    ) {
      data = this.events.filter(
        (x) =>
          this.bounds[0] <= new Date(x.eventstart).getTime() &&
          this.bounds[1] >= new Date(x.eventend).getTime()
      );
    } else {
      data = this.events;
    }
    return data;
  }
}

export const ChartStore = new ChartStoreImpl();
